import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Input, Textarea, Select } from "../components/UI";
import { Wand2, Loader2, Download, RefreshCcw, Sparkles } from "lucide-react";

const TONE = ["Bold", "Trustworthy", "Urgent", "Inspirational", "Presidential", "Grassroots"];
const PRESETS = [
  { label: "CTV Endframe — GOTV", prompt: "Connected TV ad endframe, 16:9 cinematic. 'Vote' theme, American flag motif softly blurred in background, deep navy and warm gold light. Clean space for candidate name and disclaimer strip at bottom. No specific candidate. Broadcast quality." },
  { label: "CTV Storyboard — Issue Ad", prompt: "Cinematic CTV storyboard frame, 16:9. Main street of an American town at golden hour, families, local businesses. Documentary realism, hopeful tone. Space for message overlay. Broadcast quality." },
  { label: "Fundraising Appeal", prompt: "Political fundraising banner. Dramatic navy background with a rising red-white-blue light streak, 'match deadline' urgency feel. Bold headline safe area and donate button space. No candidate likeness." },
  { label: "Voter Education", prompt: "Neutral, professional political campaign banner. American flag motif subtle in background. Deep navy and white. Space for disclaimer at bottom. No specific candidate. 1024x1024." },
  { label: "Debate Night Promo", prompt: "CTV promo frame, 16:9: empty debate stage with two podiums under dramatic spotlights, navy and cyan lighting, broadcast news aesthetic, headline safe area top third." },
];

export default function AICreativeStudio() {
  const [params] = useSearchParams();
  const conceptHeadline = params.get("headline");
  const [prompt, setPrompt] = useState(conceptHeadline
    ? `Political campaign banner for the message "${conceptHeadline}". Deep navy background with warm patriotic light, American flag motif softly blurred, clean headline safe area and disclaimer strip at bottom. No specific candidate likeness. Broadcast quality.`
    : PRESETS[0].prompt);
  const [name, setName] = useState(conceptHeadline || "");
  const [tone, setTone] = useState("Premium");
  const [audience, setAudience] = useState("");
  const [brand, setBrand] = useState("");
  const [offer, setOffer] = useState(params.get("body") || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const buildPrompt = () => {
    const parts = [prompt];
    if (tone) parts.push(`Tone: ${tone}.`);
    if (audience) parts.push(`Target audience: ${audience}.`);
    if (brand) parts.push(`Brand: ${brand}.`);
    if (offer) parts.push(`Offer/message: ${offer}.`);
    parts.push("Leave clear safe area for headline text overlay. High-end commercial advertising photography quality.");
    return parts.join(" ");
  };

  const generate = async () => {
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.post("/ai/image", {
        prompt: buildPrompt(),
        name: name || undefined,
      });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Powered by Gemini Nano Banana"
        title="AI Creative Studio"
        subtitle="Generate banner ad creatives from natural-language prompts. All output is logged and requires human approval before launch."
      />
      {conceptHeadline && (
        <div className="rounded-md border border-violet-400/25 bg-violet-400/5 px-4 py-2 text-xs text-violet-200 inline-flex items-center gap-2" data-testid="studio-prefill-chip">
          <Sparkles className="w-3.5 h-3.5" /> Prefilled from your campaign draft concept: “{conceptHeadline}” — tweak the brief and generate.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">Brief</div>
          <div className="space-y-3">
            <Input label="Creative name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Spring Bistro - Lunch" data-testid="studio-name" />
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-1.5">Quick presets</div>
              <div className="flex flex-wrap gap-1.5">
                {PRESETS.map(p => (
                  <button key={p.label} onClick={() => setPrompt(p.prompt)} data-testid={`preset-${p.label}`}
                    className="px-2.5 py-1 rounded-full text-[11px] bg-white/5 border border-white/10 text-slate-300 hover:bg-cyan-400/10 hover:border-cyan-400/30 hover:text-cyan-300 transition-colors">
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <Textarea label="Prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="studio-prompt" />
            <Select label="Tone" value={tone} onChange={(e) => setTone(e.target.value)}>
              {TONE.map(t => <option key={t}>{t}</option>)}
            </Select>
            <Input label="Audience" value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Suburban swing voters, GA-06" />
            <Input label="Campaign / entity" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Voss for State Senate" />
            <Input label="Message" value={offer} onChange={(e) => setOffer(e.target.value)} placeholder="Early voting starts Oct 14" />
            <Btn onClick={generate} disabled={loading || !prompt.trim()} className="w-full justify-center" data-testid="generate-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {loading ? "Generating…" : "Generate banner"}
            </Btn>
          </div>
        </Card>

        <Card className="lg:col-span-2">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">Preview</div>
          <div className="aspect-video bg-slate-900/60 rounded-md border border-white/10 flex items-center justify-center overflow-hidden" data-testid="preview-area">
            {loading && (
              <div className="text-center">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto" />
                <div className="mt-3 text-sm text-slate-400">Composing your creative…</div>
                <div className="mt-1 text-xs text-slate-500">This usually takes 8–20 seconds</div>
              </div>
            )}
            {!loading && !result && !error && (
              <div className="text-center">
                <Sparkles className="w-10 h-10 text-slate-700 mx-auto" />
                <div className="mt-3 text-sm text-slate-500">Your banner will appear here</div>
              </div>
            )}
            {error && <div className="text-rose-300 text-sm p-4 text-center">{error}</div>}
            {result && (
              <img src={result.image_base64} alt="Generated" className="w-full h-full object-contain" data-testid="generated-image" />
            )}
          </div>
          {result && (
            <div className="mt-4 flex items-center gap-2">
              <Btn variant="secondary" onClick={generate}><RefreshCcw className="w-4 h-4" /> Regenerate</Btn>
              <a
                href={result.image_base64}
                download={`totalreach-creative.png`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-white/5 border border-white/10 text-white hover:bg-white/10"
                data-testid="download-creative-link"
              >
                <Download className="w-4 h-4" /> Download
              </a>
              <div className="ml-auto text-xs text-slate-500">Saved to Creatives Library · awaiting approval</div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
